import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useStore } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { Divider } from '@/components/Divider/Divider.component'
import { PhotoSelector } from '@/components/PhotoSelector/PhotoSelector'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useStartNewPersonSearchMutation } from '@/store/api/NewSearch/NewSearch.query'
import { ESocialMedia } from '@/store/api/NewSearch/NewSearch.types'
import {
  useEditDigitalFootprintMutation,
  useLazyUserInfoQuery,
} from '@/store/api/Users/Users.query'
import { selectUserInfo } from '@/store/api/Users/Users.selector'
import { setUserInfo } from '@/store/api/Users/Users.slice'
import type { RootState } from '@/store/store'
import { trackReportRunInitiated } from '@/utils/analytics'

import { ConfirmInformation } from './Components/ConfirmInformation'
import { ReportLanguage } from './Components/ReportLanguage'
import { SocialMediaAccounts } from './Components/SocialMediaAccounts'
import { CheckPrivacityPopUp } from './Components/CheckPrivacityPopUp'

export const DigitalFootprintForm = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const isEditDigitalFootprint =
    location.pathname === AppRoutes.editDigitalFootprint
  const isConfirmSearchInfo = location.pathname === AppRoutes.confirmSearchInfo

  const [isChecked, setIsChecked] = useState(false)
  const [showPrivacyPopup, setShowPrivacyPopup] = useState(false)

  const dispatch = useDispatch()
  const store = useStore()

  const [triggerGetUserInfo, { data, isLoading, isSuccess }] =
    useLazyUserInfoQuery()

  const [editDigitalFootprint, { isLoading: isEditDigitalFootprintLoading }] =
    useEditDigitalFootprintMutation()

  const [startNewPersonSearch, { isLoading: isStartNewPersonSearchLoading }] =
    useStartNewPersonSearchMutation()

  useEffect(() => {
    triggerGetUserInfo()
  }, [triggerGetUserInfo])

  useEffect(() => {
    if (isSuccess && data) {
      dispatch(setUserInfo(data))
    }
  }, [isSuccess, data, dispatch])

  const handleEditDigitalFootprint = useCallback(async () => {
    try {
      // Get the latest user info from Redux store right before submitting
      const latestUserInfo = selectUserInfo(store.getState() as RootState)
      const data = {
        socialMedias: latestUserInfo?.socialMedias || [],
        pdfLanguage: latestUserInfo?.pdfLanguage || '',
      }
      await editDigitalFootprint(data).unwrap()
      navigate(AppRoutes.dashboard)
    } catch (error) {
      toast.error(t('search.common.errorFinishingSetup'))
    }
  }, [store, editDigitalFootprint, navigate])

  const handleConfirmSetupInfo = useCallback(() => {
    // Get the latest user info from Redux store right before submitting
    const latestUserInfo = selectUserInfo(store.getState() as RootState)

    const payload = {
      // User info
      name: latestUserInfo?.firstname || '',
      lastName: latestUserInfo?.lastname || '',
      email: latestUserInfo?.email || '',

      // Phone: At the moment we are not using phone until we can introduce it.
      // phone: latestUserInfo?.phoneNumber || '',

      picture: latestUserInfo?.profileImgUrl || '',

      // Social media urls
      twitterUrl:
        latestUserInfo?.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.TWITTER,
        )?.profileLink || '',
      facebookUrl:
        latestUserInfo?.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.FACEBOOK,
        )?.profileLink || '',
      instagramUrl:
        latestUserInfo?.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.INSTAGRAM,
        )?.profileLink || '',
      tikTokUrl:
        latestUserInfo?.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.TIKTOK,
        )?.profileLink || '',
      linkedInUrl:
        latestUserInfo?.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.LINKEDIN,
        )?.profileLink || '',
      pinterestUrl:
        latestUserInfo?.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.PINTEREST,
        )?.profileLink || '',
      redditUrl:
        latestUserInfo?.socialMedias?.find(
          (socialMedia) => socialMedia.name === ESocialMedia.REDDIT,
        )?.profileLink || '',

      // Topics: At the moment we are not using topics until we can introduce them.
      topics: [],
      phone: latestUserInfo?.phoneNumber || '',
    }

    // Filter out empty values (empty strings, empty arrays, null, undefined)
    const filteredPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, value]) => {
        if (typeof value === 'string') return value.trim() !== ''
        if (Array.isArray(value)) return value.length > 0
        return value !== null && value !== undefined
      }),
    )

    // Track report run initiated (user intent) before calling API
    trackReportRunInitiated()

    // Always include required fields
    startNewPersonSearch({
      ...filteredPayload,
      pdfLanguage: latestUserInfo?.pdfLanguage || '',

      // Use case: At the moment we are not using use case until we can introduce it.
    })
      .then((response) => {
        if ('data' in response) {
          const reportId = response.data.info.id
          localStorage.setItem('reportId', reportId)
          localStorage.setItem('subjectId', response.data.info.subjectId)

          navigate(AppRoutes.generatingReport)
        } else {
          toast.error(t('search.common.errorStartingNewPersonSearch'))
        }
      })
      .catch((error) => {
        toast.error(t('search.common.errorStartingNewPersonSearch'))
        catchWithSentry(error)
      })
  }, [store, startNewPersonSearch, navigate])

  const onSubmit = () => {
    if (isEditDigitalFootprint) {
      handleEditDigitalFootprint()
    } else if (isConfirmSearchInfo) {
      handleConfirmSetupInfo()
    }
  }
  const validateAndSubmit = () => {
    const latestUserInfo = selectUserInfo(store.getState() as RootState)

    if (!latestUserInfo?.lastname || latestUserInfo.lastname.trim() === '') {
      toast.info(t('search.common.pleaseEnterLastName'))
      return
    }

    if (
      !latestUserInfo?.socialMedias ||
      latestUserInfo.socialMedias.length === 0
    ) {
      toast.info(t('search.common.pleaseAddAtLeastOneSocialMedia'))
      return
    }

    if (isConfirmSearchInfo && !isChecked) {
      toast.info(t('search.common.pleaseAcceptTerms'))
      return
    }

    if (isConfirmSearchInfo) {
      setShowPrivacyPopup(true)
      return
    }

    onSubmit()
  }

  const enableSubmitButtomEditDigitalFootprint = !isEditDigitalFootprintLoading
  const enableSubmitButtomConfirmSearchInfo =
    !isEditDigitalFootprintLoading && !isStartNewPersonSearchLoading

  return (
    <WeScreen
      useNextButton={false}
      isLoading={
        isLoading ||
        isEditDigitalFootprintLoading ||
        isStartNewPersonSearchLoading
      }
      customViewStyles="max-md:p-0"
      useHeaderBackButton
      onClickBack={() => navigate(AppRoutes.dashboard)}
    >
      <div className="flex w-full h-full gap-10 items-center justify-center">
        <div className="flex flex-col p-5 gap-5 w-1/2 max-md:w-full max-lg:w-3/4 bg-baseColorHighlight rounded-2xl">
          <PhotoSelector
            initialImageUrl={data?.profilePicUrl || ''}
            fullname={data?.firstname + ' ' + data?.lastname}
          />
          <Divider />
          <ReportLanguage />
          <Divider />
          <ConfirmInformation />
          <Divider />
          <SocialMediaAccounts />
          <Divider />

          {/* We only need the check box to confirm legal stuff when we are confirming the search info */}
          {isConfirmSearchInfo && (
            <div className="flex gap-3 items-center">
              <Checkbox
                className="w-5 h-5 rounded-md"
                checked={isChecked}
                onCheckedChange={() => setIsChecked(!isChecked)}
              />
              <p className="text-neutralColor80 text-sm font-normal">
                {t('search.common.iAgreeToTerms')}
                <a
                  href={t('common.termsAndConditionsLink')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primaryColor underline"
                >
                  {t('common.termsAndConditions')}
                </a>
                {t('search.common.iAgreeToTermsAnd')}
                <a
                  href={t('common.privacyPolicyLink')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primaryColor underline"
                >
                  {t('common.privacyPolicy')}
                </a>
                .
              </p>
            </div>
          )}

          <div className="flex flex-row gap-5 w-full justify-end">
            <Button
              disabled={
                isEditDigitalFootprint
                  ? !enableSubmitButtomEditDigitalFootprint
                  : !enableSubmitButtomConfirmSearchInfo
              }
              onClick={() => {
                validateAndSubmit()
              }}
              className="font-medium text-base h-[46px] px-5 rounded-3xl py-3"
            >
              {isEditDigitalFootprint
                ? t('common.save')
                : t('search.common.verifyInformation')}
            </Button>
          </div>
        </div>
      </div>

      <CheckPrivacityPopUp
        open={showPrivacyPopup}
        onOpenChange={setShowPrivacyPopup}
        socialMedias={
          selectUserInfo(store.getState() as RootState)?.socialMedias || []
        }
        onRunReport={handleConfirmSetupInfo}
      />
    </WeScreen>
  )
}
